export interface PydanticErrorDetail {
  type: string;
  loc: (string | number)[];
  msg: string;
  input?: unknown;
  ctx?: Record<string, unknown>;
}

/**
 * Type guard that narrows an unknown `detail` value to a Pydantic validation
 * error array (as returned by FastAPI on 422 responses).
 */
export function isPydanticValidationError(detail: unknown): detail is PydanticErrorDetail[] {
  return Array.isArray(detail) && detail.length > 0 && typeof (detail[0] as PydanticErrorDetail).loc !== 'undefined';
}

/**
 * Parses a Pydantic validation error array into two buckets:
 *
 * - `fieldErrors`: keyed by the top-level field name (snake_case, as Pydantic
 *   reports it). The first error per field wins.
 * - `nonFieldErrors`: errors not tied to a specific field (e.g. model-level
 *   validators whose `loc` is `["body"]` or `["body", "__all__"]`).
 *
 * The leading `loc` segment (`"body"`, `"query"`, `"path"`, etc.) is stripped
 * before bucketing so callers work with bare field names.
 */
export function parsePydanticErrors(detail: PydanticErrorDetail[]): {
  fieldErrors: Record<string, string>;
  nonFieldErrors: string[];
} {
  const fieldErrors: Record<string, string> = {};
  const nonFieldErrors: string[] = [];

  for (const err of detail) {
    const path = err.loc.slice(1); // strip leading source segment ("body", etc.)

    if (path.length === 0 || (path.length === 1 && path[0] === '__all__')) {
      nonFieldErrors.push(err.msg);
    } else {
      const key = String(path[0]);
      // First error wins for any given field
      if (!(key in fieldErrors)) {
        fieldErrors[key] = err.msg;
      }
    }
  }

  return { fieldErrors, nonFieldErrors };
}
